import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema } from "../lib/seo";
import { TERMS_AND_CONDITIONS } from "../data/termsAndConditions";

const PAGE_PATH = "/terms-and-conditions";
const URL_RE = /(https?:\/\/[^\s]+?)(?=[.,;:)]?(?:\s|$))/g;

// Render text, turning any URL into a link without altering the visible text.
const withLinks = (text) => {
  const parts = text.split(URL_RE);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#009688] hover:underline break-all"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const bodyText = "text-base md:text-lg text-gray-300 leading-relaxed";

const Block = ({ block, index }) => {
  switch (block.type) {
    case "heading":
      return (
        <h2
          className="text-2xl md:text-3xl font-bold text-white mt-12 mb-5 scroll-mt-24"
          data-testid={`terms-heading-${index}`}
        >
          {block.text}
        </h2>
      );
    case "list": {
      if (block.style === "alpha") {
        // Matches the document's "(a)", "(b)", ... numbering format.
        return (
          <ol className={`${bodyText} list-none pl-6 md:pl-8 mb-6 space-y-2`} data-testid={`terms-list-${index}`}>
            {block.items.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-8 text-[#4EBAAA]">({String.fromCharCode(97 + i)})</span>
                <span>{withLinks(item)}</span>
              </li>
            ))}
          </ol>
        );
      }
      const ListTag = "ul";
      return (
        <ListTag
          className={`${bodyText} ${
            block.style === "alpha" ? "list-[lower-alpha]" : "list-disc"
          } pl-10 md:pl-12 mb-6 space-y-2 marker:text-[#4EBAAA]`}
          data-testid={`terms-list-${index}`}
        >
          {block.items.map((item, i) => (
            <li key={i} className="pl-1">
              {withLinks(item)}
            </li>
          ))}
        </ListTag>
      );
    }
    case "p":
    default:
      return (
        <p className={`${bodyText} mb-5 ${block.indent ? "pl-6 md:pl-8" : ""}`}>
          {block.lead && <strong className="font-semibold text-white">{block.lead}</strong>}
          {block.text && withLinks(block.text)}
        </p>
      );
  }
};

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = TERMS_AND_CONDITIONS.find((b) => b.type === "title");
  const updated = TERMS_AND_CONDITIONS.find((b) => b.type === "updated");
  const body = TERMS_AND_CONDITIONS.filter((b) => b.type !== "title" && b.type !== "updated");

  return (
    <div className="bg-[#101F27] text-white min-h-screen" data-testid="terms-and-conditions-page">
      <SEO
        {...getPageSeo(PAGE_PATH)}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Terms and Conditions", path: PAGE_PATH },
        ])}
      />

      {/* Page Header */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link
            to="/"
            className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <p className="text-base md:text-lg font-semibold text-[#009688] uppercase tracking-wide mb-4">
            Legal
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="terms-title">
            {title?.text}
          </h1>
          {updated && (
            <p className="italic text-white/70" data-testid="terms-last-updated">
              {updated.text}
            </p>
          )}
        </div>
      </section>

      {/* Agreement Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="max-w-none [&>h2:first-of-type]:mt-10" data-testid="terms-content">
            {body.map((block, i) => (
              <Block key={i} block={block} index={i} />
            ))}
          </article>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;
